import { WorkspaceFolder } from 'vscode-languageserver-protocol';
import { IDocContext } from '../document_transformer_service';
import { log } from '../log';
import { DuoProjectAccessChecker } from '../services/duo_access';
import { DuoProjectStatus } from '../services/duo_access/project_access_checker';
import { getByteSize } from '../utils/byte_size';
import { ContextResolution } from './context_resolvers/advanced_context_resolver';

type AdvancedContextFilterArgs = {
  contextResolutions: ContextResolution[];
  documentContext: IDocContext;
  byteSizeLimit: number;
  dependencies: {
    duoProjectAccessChecker: DuoProjectAccessChecker;
  };
};

type AdvanceContextFilter = (args: AdvancedContextFilterArgs) => Promise<ContextResolution[]>;

/**
 * Filters context resolutions that have empty content.
 */
const emptyContentFilter = async ({ contextResolutions }: AdvancedContextFilterArgs) => {
  return contextResolutions.filter(({ content }) => content.replace(/\s/g, '') !== '');
};

/**
 * Filters context resolutions that
 * contain a Duo project that have Duo features enabled.
 * See `DuoProjectAccessChecker` for more details.
 */
const duoProjectAccessFilter: AdvanceContextFilter = async ({
  contextResolutions,
  dependencies: { duoProjectAccessChecker },
  documentContext,
}: AdvancedContextFilterArgs) => {
  if (!documentContext?.workspaceFolder) {
    return contextResolutions;
  }
  return contextResolutions.reduce((acc, resolution) => {
    const { uri: resolutionUri } = resolution;

    const { status } = duoProjectAccessChecker.checkProjectStatus(
      resolutionUri,
      documentContext.workspaceFolder as WorkspaceFolder,
    );
    if (status === DuoProjectStatus.DuoDisabled) {
      log.warn(`Advanced Context Filter: Duo features are not enabled for ${resolutionUri}`);
      return acc;
    }
    return [...acc, resolution];
  }, [] as ContextResolution[]);
};

/**
 * Filters context resolutions that meet the byte size limit.
 * The byte size limit takes into the size of the total
 * context resolutions content + size of document content.
 */
const byteSizeLimitFilter: AdvanceContextFilter = async ({
  contextResolutions,
  documentContext,
  byteSizeLimit,
}: AdvancedContextFilterArgs) => {
  const documentSize = getByteSize(`${documentContext.prefix}${documentContext.suffix}`);

  let currentTotalSize = documentSize;

  const filteredResolutions: ContextResolution[] = [];

  for (const resolution of contextResolutions) {
    currentTotalSize += getByteSize(resolution.content);
    if (currentTotalSize > byteSizeLimit) {
      // trim the current resolution content to fit the byte size limit
      const trimmedContent = Buffer.from(resolution.content)
        .slice(0, byteSizeLimit - currentTotalSize)
        .toString();
      if (trimmedContent.length) {
        log.info(
          `Advanced Context Filter: ${resolution.uri} content trimmed to fit byte size limit: ${trimmedContent.length} bytes.`,
        );
        filteredResolutions.push({ ...resolution, content: trimmedContent });
      }
      log.debug(
        `Advanced Context Filter: Byte size limit exceeded for ${resolution.uri}. Skipping resolution.`,
      );
      break;
    }
    filteredResolutions.push(resolution);
  }
  return filteredResolutions;
};

/**
 * Advanced context filters that are applied to context resolutions.
 * @see filterContextResolutions
 */
const advancedContextFilters: AdvanceContextFilter[] = [
  emptyContentFilter,
  duoProjectAccessFilter,
  byteSizeLimitFilter,
];

/**
 * Filters context resolutions based on business logic.
 * The filters are order dependent.
 * @see advancedContextFilters
 */
export const filterContextResolutions = async ({
  contextResolutions,
  dependencies,
  documentContext,
  byteSizeLimit,
}: AdvancedContextFilterArgs): Promise<ContextResolution[]> => {
  return advancedContextFilters.reduce(async (prevPromise, filter) => {
    const resolutions = await prevPromise;
    return filter({
      contextResolutions: resolutions,
      dependencies,
      documentContext,
      byteSizeLimit,
    });
  }, Promise.resolve(contextResolutions));
};
